/**
 * 简易流程 / 审核可写：listFieldByBusId 字段授权。
 * 约定：接口调用前先全禁（只加 disabled）→ 回包后再按 domainTable 白名单调整。
 */
import {_loadFormFieldPermByBusId} from "@/api/sys/flowServer";

/**
 * 加载节点权限之后（开始节点等旧路径仍会用到）
 */
export const afterLoadFieldAclListFun = function (fieldList) {
  if (fieldList && fieldList.length > 0) {
    fieldList.forEach((itemDb) => {
      itemDb['columnName'] = itemDb['dbField']
    });
    const isCanEditor = fieldList.filter((itemDb) => {
      return (itemDb.editable + "") === "1" || (itemDb.editable + "") === "true";
    });
    const isHiddenEl = fieldList.filter((itemDb) => {
      return (itemDb.hidden + "") === "1" || (itemDb.hidden + "") === "true";
    });
    const isRequiredEl = fieldList.filter((itemDb) => {
      return (itemDb.required + "") === "1" || (itemDb.required + "") === "true";
    });
    return {isCanEditor, isHiddenEl, isRequiredEl}
  }
  return {isCanEditor: [], isHiddenEl: [], isRequiredEl: []}
}

function isAclFlagTrue(flag: any): boolean {
  return flag === true || flag === 'true' || flag === 1 || flag === '1';
}

/** 同条多标志：隐藏 > 只读(禁用) > 可编辑；都未命中则保持禁用 */
export function resolveFlowFieldAclState(itemDb: any): 'hidden' | 'disabled' | 'editable' {
  if (!itemDb) return 'disabled';
  if (isAclFlagTrue(itemDb.hidden)) return 'hidden';
  if (isAclFlagTrue(itemDb.readOnly)) return 'disabled';
  if (isAclFlagTrue(itemDb.editable)) return 'editable';
  return 'disabled';
}

/** listFieldByBusId 回包形态固定为 {code, success, data:{domainTable:{main:[...], 子表字段:[...]}}}；QueryCommand 已解包时则直接是 {domainTable} */
export function unwrapFlowAclPayload(res: any): any {
  if (res && typeof res === 'object' && res.data && typeof res.data === 'object') {
    return res.data;
  }
  return res || {};
}

/** 递归收集表单控件 key（model.key + props.key，供 formElAclInfos 命中） */
const collectFormElKeys = function (list, keyList) {
  if (!list) {
    return;
  }
  list.forEach((elItem) => {
    if (elItem && elItem.model && elItem.model.key) {
      keyList.push(elItem.model.key);
    }
    if (elItem && elItem.props && elItem.props.key) {
      keyList.push(elItem.props.key);
    }
    if (elItem && elItem.children) {
      collectFormElKeys(elItem.children, keyList);
    }
  })
}

export function useFormFlowAcl({
                                 simpleFlowAclConfigs,
                                 formElAclInfos,
                                 flowContainer,
                                 childrenList
                               }) {

  // 审核抽屉会话：表单 id 缺失时回退 busId
  const flowReviewSession = inject('flowReviewSession', null);


  /**
   * 默认全禁：只写 formElAclInfos=disabled，不改控件原有 readOnlyText 等
   * 必须在 listFieldByBusId 调用前执行，避免接口返回前短暂可写
   */
  const disableAllFormFields = function () {
    const elKeys = [];
    collectFormElKeys(childrenList, elKeys);
    const nextAcl: Record<string, string> = {...(unref(formElAclInfos) || {})};
    elKeys.forEach((elKey) => {
      if (elKey == null || elKey === '') return;
      nextAcl[elKey] = 'disabled';
    });
    formElAclInfos.value = nextAcl;
  };

  // 进入流程容器即全禁（不等待表单数据 / 权限接口）
  if (unref(flowContainer)) {
    disableAllFormFields();
  }

  /**
   * 表单数据加载后：先全禁 → 再调 listFieldByBusId → 按 domainTable 白名单放开
   */
  const afterLoadFormData = function (formObjDomain) {
    const inFlow = !!unref(flowContainer);
    const busId =
      (formObjDomain && formObjDomain.id != null && formObjDomain.id !== '')
        ? formObjDomain.id
        : (flowReviewSession && flowReviewSession.busId != null && flowReviewSession.busId !== ''
          ? flowReviewSession.busId
          : null);
    if (inFlow && busId != null && busId !== '') {
      // 接口调用前再全禁一次（子控件可能刚挂载）
      disableAllFormFields();

      _loadFormFieldPermByBusId({
        id: busId,
      }, (res) => {
        const payload = unwrapFlowAclPayload(res);
        const domainTable = payload.domainTable || {};
        // 供子表 useSubTableAclConfigs / 单元格 hooks 按业务字段名取白名单
        simpleFlowAclConfigs.value = payload;

        // 在已全禁基础上按白名单调整（仍先扫一遍全禁，防止回调前新增了控件）
        const elKeys = [];
        collectFormElKeys(childrenList, elKeys);
        const nextAcl: Record<string, string> = {...(unref(formElAclInfos) || {})};
        elKeys.forEach((elKey) => {
          if (elKey == null || elKey === '') return;
          nextAcl[elKey] = 'disabled';
        })

        // 主表白名单 domainTable.main
        const mainFields = Array.isArray(domainTable.main) ? domainTable.main : null;

        if (mainFields && mainFields.length > 0) {
          mainFields.forEach((itemDb) => {
            const fieldKey = itemDb.value || itemDb.dbField;
            if (!fieldKey) return;
            nextAcl[fieldKey] = resolveFlowFieldAclState(itemDb);
          })
        }

        // 子表容器：非 main 且有白名单列时放开容器禁用，列级再控
        Object.keys(domainTable).forEach((tableKey) => {
          if (tableKey === 'main') return;
          const list = domainTable[tableKey];
          if (!Array.isArray(list) || list.length === 0) return;
          nextAcl[tableKey] = 'editable';
        });

        formElAclInfos.value = nextAcl;
      }, 'busRecord');
    }
  }

  return {
    afterLoadFormData
  }
}
